import React from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    PriceBoostIndicator,
    RacingPostIndicator,
} from 'src/domains/sportsbook/webview/components/raceCard/tabs/Tabs.style';
import { DateTime } from 'src_common/utils/time/time';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { useCommon } from 'src/domains/common/Common';
import { EventListGroupEventItemType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';
import { EventWithCompetitionIdType } from '../../containers/sportMeetingsList/SportMeetingsList.state';
import { EventMarketItemType } from 'src_common/common/websocket2/modelsApi/EventMarkets';
import { useRenderWhenVisible } from './useRenderWhenVisible';
import { RaceLabelWrapper } from './RaceLabel.style';

const extractRaceStartTime = (race: EventListGroupEventItemType): DateTime | null => {
    return DateTime.from(race.startTime);
};

const getSliceForName = (sliceNameRaceLabel: boolean, name: string): string => {
    if (!sliceNameRaceLabel) {
        return name;
    }

    return name.slice(0, 3);
};

interface RenderLabelPropsType {
    race: EventWithCompetitionIdType;
    displayCompetition: boolean;
    isFinishedRace?: boolean;
    shouldShowPriceBoostIcon?: boolean;
    isActiveTab?: boolean;
}

const hasPriceBoostForRace = (
    marketRaceWinnerAll: EventMarketItemType[],
    isBoostedPriceAvailable: boolean
): boolean => {
    if (marketRaceWinnerAll.length === 0) {
        return false;
    }

    if (isBoostedPriceAvailable === false) {
        return false;
    }

    const winOrEwMarket = marketRaceWinnerAll.find((market) => market.display === true);

    if (winOrEwMarket === undefined) {
        return false;
    }

    const marketModel = winOrEwMarket.id2.getModel();

    if (marketModel === null) {
        return false;
    }

    return marketModel.selections.some(
        (selection) => selection.display === true && selection.isBoostedPriceVisible === true
    );
};

interface RaceLabelHeavyPropsType {
    race: EventWithCompetitionIdType;
    displayCompetition: boolean;
    shouldShowPriceBoostIcon?: boolean;
}

const RaceLabelHeavy = observer('RaceLabelHeavy', (props: RaceLabelHeavyPropsType) => {
    const { race, displayCompetition, shouldShowPriceBoostIcon } = props;
    const common = useCommon();
    const config = ConfigComponents.get(common).config;
    const { sliceNameRaceLabel } = config;

    const competition = race.competitionId.getModel();
    const getShortName = competition === null ? null : getSliceForName(sliceNameRaceLabel, competition.name);
    const marketRaceWinnerAll = race.id2.getEventMarkets();
    const hasPriceBoost = hasPriceBoostForRace(marketRaceWinnerAll ?? [], shouldShowPriceBoostIcon ?? false);

    return (
        <>
            {displayCompetition && getShortName !== null ? <> {getShortName}</> : null}
            {hasPriceBoost === true ? <PriceBoostIndicator /> : null}
        </>
    );
});

export const RaceLabel = observer('RaceLabel', (props: RenderLabelPropsType) => {
    const { race, displayCompetition, isFinishedRace, shouldShowPriceBoostIcon, isActiveTab } = props;
    const renderImmediately = isActiveTab === true;
    const [setVisibilityRef, shouldRenderHeavy] = useRenderWhenVisible(renderImmediately);

    const startTime = extractRaceStartTime(race);

    return (
        <RaceLabelWrapper ref={setVisibilityRef}>
            {isFinishedRace === true ? <RacingPostIndicator /> : null}
            {startTime?.format('HH:mm')}
            {shouldRenderHeavy ? (
                <RaceLabelHeavy
                    race={race}
                    displayCompetition={displayCompetition}
                    shouldShowPriceBoostIcon={shouldShowPriceBoostIcon}
                />
            ) : null}
        </RaceLabelWrapper>
    );
});
